import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { Title } from '@/components/common/Title';
import { PortableText } from '@/components/common/utils';
import { Col, Row } from '@/components/layout/Grid';
import {
  getBackgroundColor,
  getDecorativeImage,
  getDecorativePosition,
  getTextColor,
  getTokenColor,
} from '@/components/page/courses/ProgrammeHero/helper';

import type { HeadingSanityBlockType } from '@/components/common/Heading';
import type { SanityBlock, SanityKeyed } from '@/types/sanity';

export type ProgrammeHeroProps = {
  decorative?:
    | 'advanced-data-fellowship-topup'
    | 'ai-for-business-value'
    | 'ai-powered-productivity'
    | 'data-fellowship'
    | 'advanced-software-engineering'
    | 'software-engineering'
    | 'business-transformation'
    | 'transformative-leadership'
    | 'digital-business-accelerator'
    | 'project-management'
    | 'applied-data-engineering'
    | 'data-insights-for-business-decisions'
    | 'advance-data-fellowship'
    | 'technology-consulting';
  heading?: HeadingSanityBlockType;
  subheading?: Array<SanityKeyed<SanityBlock>>;
  caption?: Array<SanityKeyed<SanityBlock>>;
};

const ProgrammeHeroContent = ({
  heading,
  subheading,
  caption,
  decorative,
}: ProgrammeHeroProps) => {
  const textColor = getTextColor(decorative);

  return (
    <TextWrapper textColor={textColor}>
      <Title
        size="md"
        variant="hero"
        heading={heading}
        desktopAlignment="start"
        mobileAlignment="start"
      />
      {!!subheading && (
        <SubheadingWrapper textColor={textColor}>
          <PortableText value={subheading} />
        </SubheadingWrapper>
      )}
      {!!caption && (
        <CaptionContainer textColor={textColor}>
          <PortableText value={caption} />
        </CaptionContainer>
      )}
    </TextWrapper>
  );
};

export const ProgrammeHero = ({
  heading,
  subheading,
  caption,
  decorative,
}: ProgrammeHeroProps) => {
  const backgroundColour = getBackgroundColor(getTokenColor(decorative));
  const decorativeImage = getDecorativeImage(decorative);
  const decorativePosition = getDecorativePosition(decorative);
  return (
    <HeroWrapper backgroundColour={backgroundColour}>
      <DesktopContainer>
        <ProgrammeHeroContent
          heading={heading}
          subheading={subheading}
          caption={caption}
          decorative={decorative}
        />
        <StripesContainerDesktop
          decorativeImage={decorativeImage.desktop}
          decorativePosition={decorativePosition.desktop}
        />
      </DesktopContainer>
      <MobileContainer>
        <DecorativeContainerMobile backgroundColour={backgroundColour}>
          <StripesLinesMobile
            decorativeImage={decorativeImage.mobile}
            decorativePosition={decorativePosition.mobile}
          />
        </DecorativeContainerMobile>
        <HeadingMobileContainer>
          <Row>
            <Col sizes={{ sm: 10 }} start={{ sm: 2 }}>
              <ProgrammeHeroContent
                heading={heading}
                subheading={subheading}
                caption={caption}
              />
            </Col>
          </Row>
        </HeadingMobileContainer>
      </MobileContainer>
    </HeroWrapper>
  );
};

const HeroWrapper = styled.div<{ backgroundColour?: string }>`
  display: flex;
  flex-direction: column;
  align-items: left;
  gap: var(--space-block-md);
  border-radius: var(--radius-component-xxl);
  padding-right: 12px;
  padding-left: 12px;
  margin-right: -20px;
  margin-left: -20px;

  ${screen.sm} {
    margin-right: -28px;
    margin-left: -28px;
  }

  ${screen.md} {
    background-color: ${({ backgroundColour }) => backgroundColour};
    flex-direction: row;
    padding-right: 48px;
    padding-left: 48px;
    padding-top: var(--space-block-lg);
    padding-bottom: var(--space-block-lg);
    margin-left: -48px;
    margin-right: -48px;
  }

  ${screen.lg} {
    flex-direction: row;
    padding-right: 56px;
    padding-left: 56px;
    padding-top: var(--space-block-lg);
    padding-bottom: var(--space-block-lg);
    margin-left: -56px;
    margin-right: -56px;
  }
`;

const DesktopContainer = styled.div`
  display: none;

  ${screen.md} {
    display: flex;
    width: 100%;
  }
`;

const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 30px;

  ${screen.md} {
    display: none;
  }
`;

const SubheadingWrapper = styled.div<{ textColor: { subheading: string } }>`
  text-align: left;
  align-self: flex-start;

  p {
    font-size: var(--font-size-4);
    color: ${({ textColor }) => textColor.subheading};
  }
`;

const TextWrapper = styled.div<{
  textColor: { heading: string; link: string };
}>`
  display: flex;
  flex-direction: column;
  margin-top: -54px;
  gap: var(--space-component-xl);

  span {
    background: var(--background-subtle);
    box-decoration-break: clone;
    padding: 8px;
    border-radius: var(--radius-component-sm);
    color: ${({ textColor }) => textColor.heading};
  }

  p > a {
    font-weight: var(--font-weight-medium);
    color: ${({ textColor }) => textColor.link};
    :visited {
      color: ${({ textColor }) => textColor.link};
    }
    :hover {
      text-decoration-color: ${({ textColor }) => textColor.link};
    }
  }

  h1 {
    margin-left: -8px;
  }

  ${screen.sm} {
    margin-top: -50px;
    span {
      padding: 8px;
    }
  }

  ${screen.md} {
    position: relative;
    margin-top: 0;
    width: 100%;
    flex-direction: column;
    z-index: 1;

    span {
      background: inherit;
    }
  }
`;

const DecorativeContainerMobile = styled.div<{ backgroundColour?: string }>`
  display: flex;
  height: 160px;
  background-color: ${({ backgroundColour }) => backgroundColour};
  border-radius: var(--radius-component-xl);
  justify-content: flex-end;
  margin-right: -8px;
  margin-left: -8px;

  ${screen.md} {
    display: none;
  }
`;

const StripesLinesMobile = styled.div<{
  decorativeImage?: string;
  decorativePosition?: string;
}>`
  background-image: ${({ decorativeImage }) => decorativeImage};
  background-position: ${({ decorativePosition }) => decorativePosition};

  background-repeat: no-repeat;
  width: 98%;
  height: 100%;
  border-radius: 0 var(--radius-component-xl) var(--radius-component-xl) 0;
`;

const StripesContainerDesktop = styled.div<{
  decorativeImage?: string;
  decorativePosition: string;
}>`
  ${screen.md} {
    display: flex;

    background-image: ${({ decorativeImage }) => decorativeImage};
    background-position: ${({ decorativePosition }) => decorativePosition};
    background-repeat: no-repeat;

    border-radius: 0 var(--radius-component-xxl) var(--radius-component-xxl) 0;
    width: 100%;

    margin-right: -48px;
    margin-top: calc(var(--space-block-lg) * -1);
    margin-bottom: calc(var(--space-block-lg) * -1);
  }

  ${screen.lg} {
    margin-right: -56px;
    margin-top: calc(var(--space-block-lg) * -1);
    margin-bottom: calc(var(--space-block-lg) * -1);
  }
`;

const CaptionContainer = styled.div<{ textColor: { caption: string } }>`
  width: 100%;
  text-align: left;

  > p {
    font-size: var(--font-size-1);
    color: ${({ textColor }) => textColor.caption};
  }
`;

const HeadingMobileContainer = styled.div`
  margin-right: 0.5rem;
  margin-left: 0.5rem;

  ${screen.sm} {
    margin-right: 1rem;
    margin-left: 1rem;
  }
`;
